import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import PageSeo from "../components/PageSeo";
import "./Bikes.css";

const SORT_OPTIONS = [
  { value: "popular", label: "Most Popular" },
  { value: "name_asc", label: "Name A-Z" },
  { value: "name_desc", label: "Name Z-A" },
  { value: "small_first", label: "Small Community First" },
];

export default function Bikes() {
  const navigate = useNavigate();
  const { BIKE_CATEGORIES } = useApp();
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("popular");

  const total = useMemo(
    () => BIKE_CATEGORIES.reduce((sum, category) => sum + Number(category.count || 0), 0),
    [BIKE_CATEGORIES]
  );

  const filtered = useMemo(() => {
    const list = BIKE_CATEGORIES.filter(
      (category) =>
        category.name.toLowerCase().includes(search.toLowerCase()) ||
        category.examples.some((example) => example.toLowerCase().includes(search.toLowerCase()))
    );

    const sorted = [...list];
    if (sortBy === "name_asc") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      return sorted;
    }
    if (sortBy === "name_desc") {
      sorted.sort((a, b) => b.name.localeCompare(a.name));
      return sorted;
    }
    if (sortBy === "small_first") {
      sorted.sort((a, b) => Number(a.count || 0) - Number(b.count || 0));
      return sorted;
    }
    sorted.sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
    return sorted;
  }, [BIKE_CATEGORIES, search, sortBy]);

  const topSearchCards = useMemo(() => {
    const cards = BIKE_CATEGORIES.flatMap((category) =>
      category.examples.slice(0, 2).map((example, index) => ({
        id: `${category.id}-${example}-${index}`,
        name: example,
        category: category.name,
        audience: Math.round((Number(category.count || 0) / Math.max(total, 1)) * 100),
      }))
    );
    return cards.slice(0, 12);
  }, [BIKE_CATEGORIES, total]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "MotoHub India Bike Categories",
    description:
      "Explore popular bike categories in India including commuter, cruiser, sports, scooter, adventure, and electric bikes.",
    url: "https://motohubindia.com/bikes",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: BIKE_CATEGORIES.map((category, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: category.name,
      })),
    },
  };

  return (
    <>
      <PageSeo
        title="Bike Garage India | Explore Motorcycle Categories & Popular Bikes | MotoHub India"
        description="Explore bike categories in India with community trends, popular examples, and rider preferences. Find commuter, cruiser, sports, scooter, adventure, and electric bikes on MotoHub India."
        keywords={[
          "bike categories India",
          "motorcycle types India",
          "best bikes India",
          "commuter bikes India",
          "sports bikes India",
          "cruiser bikes India",
          "electric bikes India",
          "MotoHub India bikes",
        ]}
        path="/bikes"
        image="/og-image.jpg"
        schema={schema}
      />

      <div className="bikes-page">
        <div className="bikes-shell">
          <header className="bikes-hero">
            <p className="bikes-breadcrumb">MotoHub India > Bike Garage</p>
            <h1 className="bikes-title">Bike Garage India</h1>
            <p className="bikes-subtitle">
              {total.toLocaleString("en-IN")} registered bikes across {BIKE_CATEGORIES.length} categories. Search, compare and find the right segment.
            </p>

            <div className="bikes-controls">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search bikes or categories..."
                className="bikes-search"
                type="text"
              />
              <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="bikes-sort">
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </header>

          <section className="bikes-stats-row">
            <div className="bikes-stat">
              <p>{BIKE_CATEGORIES.length}</p>
              <span>Categories</span>
            </div>
            <div className="bikes-stat">
              <p>{total.toLocaleString("en-IN")}</p>
              <span>Riders Mapped</span>
            </div>
            <div className="bikes-stat">
              <p>{filtered.length}</p>
              <span>Visible Results</span>
            </div>
          </section>

          <section className="bikes-grid">
            {filtered.map((category) => {
              const pct = Math.round((Number(category.count || 0) / Math.max(total, 1)) * 100);
              return (
                <article key={category.id} onClick={() => setSelected(category)} className="bike-cat-card">
                  <div className="bike-cat-top">
                    <div className="bike-cat-icon">{category.icon}</div>
                    <div className="bike-cat-count">
                      <strong style={{ color: category.color }}>{Number(category.count || 0).toLocaleString("en-IN")}</strong>
                      <span>riders</span>
                    </div>
                  </div>

                  <h3>{category.name}</h3>
                  <p>{category.examples.join(", ")}</p>

                  <div className="bike-progress">
                    <div className="bike-progress-fill" style={{ width: `${pct}%`, background: category.color }} />
                  </div>
                  <small>{pct}% of community</small>
                </article>
              );
            })}
          </section>

          <section className="bikes-top-searches">
            <div className="bikes-section-head">
              <h2>Top Searched Bikes</h2>
              <button type="button" onClick={() => navigate("/compare")} className="bikes-link-btn">
                Compare Bikes
              </button>
            </div>
            <div className="bikes-mini-grid">
              {topSearchCards.map((card) => (
                <article key={card.id} className="bike-mini-card">
                  <h4>{card.name}</h4>
                  <p>{card.category}</p>
                  <span>{card.audience}% audience interest</span>
                </article>
              ))}
            </div>
          </section>

          <section className="bikes-seo-block">
            <h2>Explore Bike Categories in India</h2>
            <p>
              MotoHub India helps riders explore bike types with real community context. Compare categories by rider adoption, popular models, and real usage trends before you buy.
            </p>
            <p>
              From city-friendly commuters and scooters to touring-ready adventure bikes and performance-focused sports bikes, this page gives you a clearer category-level view.
            </p>
            <p>
              Use this as your starting point, then move to compare and calculator tools for deeper decision making on budget, mileage, and ownership costs.
            </p>
          </section>
        </div>
      </div>

      {selected ? (
        <div className="bike-modal-overlay" onClick={() => setSelected(null)} role="presentation">
          <div className="bike-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <button type="button" className="bike-modal-close" onClick={() => setSelected(null)}>
              Close
            </button>
            <div className="bike-modal-head">
              <span className="bike-modal-icon">{selected.icon}</span>
              <div>
                <h3>{selected.name}</h3>
                <p>{Number(selected.count || 0).toLocaleString("en-IN")} riders in this segment</p>
              </div>
            </div>
            <p className="bike-modal-desc">{selected.desc}</p>
            <div className="bike-modal-tags">
              {selected.examples.map((example) => (
                <span key={example}>{example}</span>
              ))}
            </div>
            <div className="bike-modal-actions">
              <button type="button" onClick={() => navigate("/compare")} className="bikes-link-btn">
                Compare in this Segment
              </button>
              <button type="button" onClick={() => navigate("/calculator")} className="bikes-link-btn muted">
                Open Calculators
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
